import { useState } from 'react';

interface RecipientInputProps {
  designId: number;
  text: string;
  onComplete: (username: string) => void;
  onBack: () => void;
}

const designStyles = {
  1: { bg: 'from-purple-900 via-pink-900 to-purple-900', textColor: 'text-pink-300' },
  2: { bg: 'from-pink-100 via-rose-100 to-pink-50', textColor: 'text-gray-800' },
  3: { bg: 'from-yellow-300 via-orange-300 to-pink-300', textColor: 'text-gray-900' },
  4: { bg: 'from-indigo-500 via-purple-500 to-pink-500', textColor: 'text-white' },
  5: { bg: 'from-blue-200 via-purple-200 to-pink-200', textColor: 'text-gray-800' }
};

export default function RecipientInput({ designId, text, onComplete, onBack }: RecipientInputProps) {
  const [username, setUsername] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const style = designStyles[designId as keyof typeof designStyles];

  const validateUsername = (value: string) => {
    const cleaned = value.replace(/^@/, '');
    setUsername(cleaned);
    
    const isValidFormat = /^[a-zA-Z0-9_]{5,32}$/.test(cleaned);
    setIsValid(isValidFormat);
  };

  const handleSubmit = () => {
    if (isValid) {
      onComplete(username);
    }
  };

  return (
    <div className="animate-slide-in">
      <div className="text-center mb-8">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-gray-400 hover:text-[#2AABEE] transition-colors duration-300 mb-6 font-medium cursor-pointer"
        >
          <i className="ri-arrow-left-line text-xl"></i>
          Назад к тексту
        </button>
        <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
          Кому отправляем?
        </h2>
        <p className="text-lg text-gray-400">
          Укажи Telegram никнейм получателя 💌
        </p>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input section */}
          <div className="space-y-6">
            <div className="bg-[#232E3C] rounded-3xl shadow-xl p-8 border border-[#2C3847]">
              <label className="block text-lg font-bold text-white mb-4">
                Telegram никнейм
              </label>
              
              <div className="relative">
                <div className="absolute left-6 top-1/2 -translate-y-1/2 text-2xl text-gray-500 pointer-events-none z-10">
                  @
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => validateUsername(e.target.value)}
                  placeholder="username"
                  className="w-full pl-14 pr-16 py-5 bg-[#17212b] border-2 border-[#2C3847] rounded-2xl focus:border-[#2AABEE] focus:ring-4 focus:ring-[#2AABEE]/20 outline-none transition-all duration-300 text-lg font-medium cursor-text text-white placeholder-gray-500"
                  autoComplete="off"
                />
                {isValid && (
                  <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-xl"></i>
                    </div>
                  </div>
                )}
              </div>

              {username && !isValid && (
                <p className="mt-3 text-sm text-rose-400 flex items-center gap-2">
                  <i className="ri-error-warning-fill"></i>
                  Никнейм должен содержать 5-32 символа (буквы, цифры, _)
                </p>
              )}

              {isValid && (
                <p className="mt-3 text-sm text-green-400 flex items-center gap-2">
                  <i className="ri-check-double-fill"></i>
                  Отлично! Никнейм корректный
                </p>
              )}

              {/* Info box */}
              <div className="mt-6 p-6 bg-[#17212b] rounded-2xl border border-[#2C3847]">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-2">Как это работает?</h4>
                    <ul className="space-y-2 text-sm text-gray-400">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-[#2AABEE] mt-0.5"></i>
                        <span>Получатель получит уведомление в Telegram</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-[#2AABEE] mt-0.5"></i>
                        <span>Валентинка будет анонимной (по желанию)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-[#2AABEE] mt-0.5"></i>
                        <span>Мгновенная доставка</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Preview toggle */}
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="mt-6 w-full py-4 bg-[#17212b] hover:bg-[#2C3847] rounded-2xl font-medium text-gray-300 transition-colors duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer border border-[#2C3847]"
              >
                <i className={`ri-eye-${showPreview ? 'off' : 'fill'} text-xl`}></i>
                {showPreview ? 'Скрыть превью' : 'Показать превью'}
              </button>
            </div>

            <button
              onClick={handleSubmit}
              disabled={!isValid}
              className={`w-full py-5 rounded-2xl font-bold text-lg transition-all duration-300 whitespace-nowrap ${
                isValid
                  ? 'bg-gradient-to-r from-[#2AABEE] to-[#229ED9] text-white hover:shadow-2xl hover:scale-105 cursor-pointer'
                  : 'bg-[#2C3847] text-gray-500 cursor-not-allowed'
              }`}
            >
              Отправить валентинку →
            </button>
          </div>

          {/* Preview section */}
          <div className={`transition-all duration-500 ${showPreview ? 'opacity-100' : 'opacity-0 lg:opacity-100'}`}>
            <div className="bg-[#232E3C] rounded-3xl shadow-xl p-8 lg:sticky lg:top-8 border border-[#2C3847]">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <i className="ri-telegram-fill text-[#2AABEE]"></i>
                Как увидит получатель
              </h3>
              
              {/* Telegram message preview */}
              <div className="bg-[#17212b] rounded-2xl p-6 space-y-4 border border-[#2C3847]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center">
                    <i className="ri-heart-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="font-bold text-white">Моя Валентинка</p>
                    <p className="text-sm text-gray-500">Бот</p>
                  </div>
                </div>

                <div className="bg-[#232E3C] rounded-2xl p-4 shadow-sm border border-[#2C3847]">
                  <p className="text-gray-300 mb-3">
                    💌 Тебе пришла валентинка!
                  </p>
                  
                  <div className={`w-full aspect-[4/5] bg-gradient-to-br ${style.bg} rounded-2xl overflow-hidden shadow-lg mb-3 relative`}>
                    <div className="absolute inset-0 flex items-center justify-center p-6">
                      <p className={`text-lg font-medium text-center ${style.textColor}`}>
                        {text || 'Твоё сообщение'}
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-500 italic">
                    От тайного поклонника ❤️
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="bg-[#17212b] rounded-xl p-4 text-center border border-[#2C3847]">
                  <p className="text-3xl font-bold text-[#2AABEE]">98%</p>
                  <p className="text-sm text-gray-400 mt-1">Доставка</p>
                </div>
                <div className="bg-[#17212b] rounded-xl p-4 text-center border border-[#2C3847]">
                  <p className="text-3xl font-bold text-[#2AABEE]">&lt;1мин</p>
                  <p className="text-sm text-gray-400 mt-1">Отправка</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Viral trigger */}
        <div className="mt-12 text-center">
          <div className="inline-block bg-gradient-to-r from-yellow-400 via-pink-400 to-purple-400 rounded-full p-1 shadow-lg animate-pulse">
            <div className="bg-[#232E3C] rounded-full px-8 py-4">
              <p className="text-white font-medium">
                🎯 <span className="font-bold">Интрига:</span> Получатель не узнает отправителя до получения!
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slide-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}